import React from 'react'
import { IoChevronBack, IoChevronForward, IoCheckmark, IoClose } from 'react-icons/io5'
import type { LayoutOverride } from '../types'
import { mergeStyles } from './layoutUtils'
import './animations.css'

export interface TutorialControlsProps {
  onPrev: () => void
  onNext: () => void
  onSkip: () => void
  onFinish: () => void
  isFirst: boolean
  isLast: boolean
  canNext?: boolean
  canSkip?: boolean
  uiPosition?: 'default' | 'center' | 'top' | 'center-up' | 'center-down' | 'center-inverted'
  extraButtons?: React.ReactNode
  layoutOverride?: LayoutOverride['controls']
  enableStaggeredAnimation?: boolean  // ✅ NOVO: Ativa animação pop-in escalonada
}

// Botão primário (Próximo, Concluir)
const btnPrimary: React.CSSProperties = {
  padding: '10px 20px',
  borderRadius: 8,
  background: 'linear-gradient(135deg, #8B5CF6, #7C3AED)',
  color: '#fff',
  border: 'none',
  cursor: 'pointer',
  fontWeight: 600,
  fontSize: 14,
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  transition: 'all 0.2s ease-out',
  boxShadow: '0 2px 8px rgba(139, 92, 246, 0.3)',
}

// Botão secundário (Voltar)
const btnSecondary: React.CSSProperties = {
  padding: '10px 20px',
  borderRadius: 8,
  background: 'transparent',
  color: '#8B5CF6',
  border: '2px solid #8B5CF6',
  cursor: 'pointer',
  fontWeight: 600,
  fontSize: 14,
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  transition: 'all 0.2s ease-out',
}

// Botão terciário (Pular)
const btnTertiary: React.CSSProperties = {
  padding: '10px 16px',
  borderRadius: 8,
  background: 'transparent',
  color: '#6B7280',
  border: 'none',
  cursor: 'pointer',
  fontWeight: 500,
  fontSize: 14,
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  transition: 'all 0.2s ease-out',
}

const TutorialControls: React.FC<TutorialControlsProps> = ({ onPrev, onNext, onSkip, onFinish, isFirst, isLast, canNext = true, canSkip = true, uiPosition = 'default', extraButtons, layoutOverride, enableStaggeredAnimation = false }) => {
  const containerStyle: React.CSSProperties = (() => {
    // Estilos padrão baseados em uiPosition
    let baseStyle: React.CSSProperties
    
    if (uiPosition === 'center-up') {
      baseStyle = {
        position: 'fixed',
        top: 'calc(45% + 140px)',
        left: 0,
        right: 0,
        zIndex: 12000,
        display: 'flex',
        gap: 8,
        justifyContent: 'center',
        alignItems: 'center',
        // Animação aplicada via className quando staggered, ou inline quando padrão
        ...(!enableStaggeredAnimation && {
          animation: 'slideInFromBottom 0.3s ease-out 0.2s forwards',
          opacity: 0,
        }),
      }
    } else if (uiPosition === 'center-down') {
      baseStyle = {
        position: 'fixed',
        top: 'calc(60% + 180px)',
        left: 0,
        right: 0,
        zIndex: 12000,
        display: 'flex',
        gap: 8,
        justifyContent: 'center',
        alignItems: 'center',
        ...(!enableStaggeredAnimation && {
          animation: 'slideInFromBottom 0.3s ease-out 0.2s forwards',
          opacity: 0,
        }),
      }
    } else if (uiPosition === 'center') {
      baseStyle = {
        position: 'fixed',
        top: 'calc(58% + 190px)',
        left: 0,
        right: 0,
        zIndex: 12000,
        display: 'flex',
        gap: 8,
        justifyContent: 'center',
        alignItems: 'center',
        ...(!enableStaggeredAnimation && {
          animation: 'slideInFromBottom 0.3s ease-out 0.2s forwards',
          opacity: 0,
        }),
      }
    } else if (uiPosition === 'top') {
      baseStyle = {
        position: 'fixed',
        top: 232,
        left: 0,
        right: 0,
        zIndex: 12000,
        display: 'flex',
        gap: 8,
        justifyContent: 'center',
        alignItems: 'center',
        ...(!enableStaggeredAnimation && {
          animation: 'slideInFromBottom 0.3s ease-out 0.2s forwards',
          opacity: 0,
        }),
      }
    } else if (uiPosition === 'center-inverted') {
      // ✅ NOVO: Posição invertida - botões no topo (acima do balão e Apis)
      baseStyle = {
        position: 'fixed',
        top: 'calc(58% - 220px)',  // Acima do conjunto (balão + Apis)
        left: 0,
        right: 0,
        zIndex: 12000,
        display: 'flex',
        gap: 8,
        justifyContent: 'center',
        alignItems: 'center',
        ...(!enableStaggeredAnimation && {
          animation: 'slideInFromBottom 0.3s ease-out 0.2s forwards',
          opacity: 0,
        }),
      }
    } else {
      baseStyle = {
        position: 'fixed',
        right: 16,
        bottom: 16,
        zIndex: 12000,
        display: 'flex',
        gap: 8,
        ...(!enableStaggeredAnimation && {
          animation: 'fadeInScale 0.3s ease-out 0.2s forwards',
          opacity: 0,
        }),
      }
    }
    
    // Aplicar overrides se presentes
    return mergeStyles(baseStyle, layoutOverride)
  })()

  return (
    <div 
      style={containerStyle} 
      className={enableStaggeredAnimation ? 'tutorial-staggered-controls' : undefined}
      data-tutorial-ui="1" 
      aria-describedby="tutorial-bubble"
    >
      {extraButtons}
      {!isFirst && (
        <button 
          type="button" 
          style={btnSecondary} 
          className="tutorial-btn-hover"
          onClick={onPrev} 
          aria-label="Voltar" 
          aria-describedby="tutorial-bubble"
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'scale(1.05)'
            e.currentTarget.style.backgroundColor = 'rgba(139, 92, 246, 0.1)'
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'scale(1)'
            e.currentTarget.style.backgroundColor = 'transparent'
          }}
        >
          <IoChevronBack size={16} />
          Voltar
        </button>
      )}
      {!isLast && (
        <button 
          type="button" 
          style={{ ...btnPrimary, opacity: canNext ? 1 : 0.5, cursor: canNext ? 'pointer' : 'not-allowed' }} 
          className={canNext ? 'tutorial-btn-hover' : ''}
          onClick={onNext} 
          aria-label="Próximo" 
          aria-describedby="tutorial-bubble" 
          disabled={!canNext}
          onMouseEnter={(e) => {
            if (canNext) {
              e.currentTarget.style.transform = 'scale(1.05)'
              e.currentTarget.style.filter = 'brightness(1.1)'
            }
          }}
          onMouseLeave={(e) => {
            if (canNext) {
              e.currentTarget.style.transform = 'scale(1)'
              e.currentTarget.style.filter = 'brightness(1)'
            }
          }}
        >
          Próximo
          <IoChevronForward size={16} />
        </button>
      )}
      {!isLast && (
        <button
          type="button"
          style={{ ...btnTertiary, opacity: canSkip ? 1 : 0.5, cursor: canSkip ? 'pointer' : 'not-allowed' }}
          className={canSkip ? 'tutorial-btn-hover' : ''}
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); try { console.info('[TutorialControls] Pular clicado'); } catch { /* ignore */ } onSkip(); }}
          aria-label="Pular"
          aria-describedby="tutorial-bubble"
          disabled={!canSkip}
          onMouseEnter={(e) => {
            if (canSkip) {
              e.currentTarget.style.transform = 'scale(1.05)'
              e.currentTarget.style.color = '#111'
            }
          }}
          onMouseLeave={(e) => {
            if (canSkip) {
              e.currentTarget.style.transform = 'scale(1)'
              e.currentTarget.style.color = '#6B7280'
            }
          }}
        >
          Pular
          <IoClose size={14} />
        </button>
      )}
      {isLast && (
        <button 
          type="button" 
          style={btnPrimary} 
          className="tutorial-btn-hover"
          onClick={onFinish} 
          aria-label="Concluir" 
          aria-describedby="tutorial-bubble"
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'scale(1.05)'
            e.currentTarget.style.filter = 'brightness(1.1)'
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'scale(1)'
            e.currentTarget.style.filter = 'brightness(1)'
          }}
        >
          Concluir
          <IoCheckmark size={16} />
        </button>
      )}
    </div>
  )}

export default TutorialControls


